import {InfiniteBrandSelector} from '@rdcomponents/brand-select/infinite-brand-selector';
import {Radio, RadioChangeEvent} from 'antd';
import {CSSProperties, useCallback, useEffect, useState} from 'react';

import {useLocale} from '../../provider/locale/locale-hook';
import {
    PostSelectorCompanyType,
    UseCompaniesSelectorHookType,
} from '../../service/feature-companies/feature-companies-type';
import {getEnumValueEnsure} from '../../util/enum';
import {Text} from '../text/text';

import {PublicationWizardStep} from './publication-wizard-step';

export enum CompanySelectionModeEnum {
    Manual = 'manual',
    AllBrandCompanies = 'brand',
}

type PropsType = {
    companiesSelector: UseCompaniesSelectorHookType<PostSelectorCompanyType>;
    mode: CompanySelectionModeEnum;
    onModeChange: (mode: CompanySelectionModeEnum) => void;
    onCancel: VoidFunction;
    onStepFinished: VoidFunction;
    onStepActivated: (width: number) => void;
    width: number;
};

// Matches the stories mode-selection step ($spacer-4xl) so every creation modal aligns identically.
const containerStyle: CSSProperties = {marginTop: 40, minHeight: 200};
const selectorStyle: CSSProperties = {display: 'flex', flexDirection: 'column', gap: 8};
const optionStyle: CSSProperties = {display: 'inline-flex', flexDirection: 'row', gap: 8};
const brandWrapperStyle: CSSProperties = {marginTop: 16, maxWidth: 240};

// Shared "choose companies or brands" step (same form as stories), without the auto-add feature:
// picking a brand simply selects all its current companies into the selector.
export function PublicationModeSelectionStep(props: PropsType): JSX.Element {
    const {companiesSelector, mode, onModeChange, onCancel, onStepFinished, onStepActivated, width} = props;

    const {getLocalizedString} = useLocale();
    const [selectedBrand, setSelectedBrand] = useState<number | null>(null);

    useEffect(() => {
        onStepActivated(width);
    }, [onStepActivated, width]);

    const handleModeChange = useCallback(
        (event: RadioChangeEvent) => {
            onModeChange(
                getEnumValueEnsure(CompanySelectionModeEnum, event.target.value, CompanySelectionModeEnum.Manual)
            );
            setSelectedBrand(null);
        },
        [onModeChange]
    );

    const handleNext = useCallback(async () => {
        if (mode === CompanySelectionModeEnum.AllBrandCompanies && selectedBrand) {
            await companiesSelector.deselectAll();
            companiesSelector.selectFiltered({brand_ids: [selectedBrand]});
        }

        onStepFinished();
    }, [companiesSelector, mode, onStepFinished, selectedBrand]);

    const isNextDisabled = mode === CompanySelectionModeEnum.AllBrandCompanies && !selectedBrand;

    return (
        <PublicationWizardStep
            leftFooterButtonConfig={{langKeyType: 'BUTTON__CANCEL', onClick: onCancel}}
            rightFooterButtonConfig={{disabled: isNextDisabled, langKeyType: 'BUTTON__NEXT', onClick: handleNext}}
        >
            <div style={containerStyle}>
                <Radio.Group onChange={handleModeChange} style={selectorStyle} value={mode}>
                    <Radio value={CompanySelectionModeEnum.Manual}>
                        <Text large stringKey="YANDEX_STORY__MODE_SELECTION__MANUAL" />
                    </Radio>
                    <Radio value={CompanySelectionModeEnum.AllBrandCompanies}>
                        <span style={optionStyle}>
                            <Text large stringKey="YANDEX_STORY__MODE_SELECTION__AUTO" />
                        </span>
                    </Radio>
                </Radio.Group>

                {mode === CompanySelectionModeEnum.AllBrandCompanies && (
                    <div style={brandWrapperStyle}>
                        <InfiniteBrandSelector
                            onChange={setSelectedBrand}
                            placeholder={getLocalizedString('YANDEX_STORY__MODE_SELECTION__SELECT_BRAND')}
                            style={{width: '100%'}}
                            value={selectedBrand}
                        />
                    </div>
                )}
            </div>
        </PublicationWizardStep>
    );
}
